import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import {
  classifyAppLink,
  getOwningTab,
  isTabRoot,
} from 'helpers/appNavigation';
import { addNativeListener, callNative } from 'helpers/nativeApp';
import { trackEvent } from 'helpers/Analytics';
import {
  isAppOffline,
  markContentLoaded,
  setOnline,
} from 'redux/modules/nativeApp';
import { syncQueuedMoves } from 'redux/modules/jobTracker';
import { goBackInApp, trackAppHistory } from './appHistory';

const ignore = () => {};

/** Dark status bar text on the white app chrome. */
function useStatusBar() {
  useEffect(() => {
    // SSR sets this already; covers a page that rendered client-side only.
    document.body.classList.add('app-mode');
    callNative('StatusBar', 'setStyle', { style: 'LIGHT' }).catch(ignore);
  }, []);
}

/**
 * Connection changes after the first load (a failed launch is the bundled
 * offline.html). Uses the Network plugin, else the browser's own events.
 */
function useNetworkStatus() {
  const dispatch = useDispatch();
  const offline = useSelector(isAppOffline);

  useEffect(() => {
    const update = (connected) => dispatch(setOnline(connected));

    callNative('Network', 'getStatus')
      .then(({ connected }) => update(connected))
      .catch(() => update(navigator.onLine));

    const listener = addNativeListener(
      'Network',
      'networkStatusChange',
      ({ connected }) => update(connected)
    );
    if (listener) return () => listener.remove();

    const goOnline = () => update(true);
    const goOffline = () => update(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, [dispatch]);

  // Each screen opened online is what the offline banner later says loaded.
  const { pathname } = useLocation();
  useEffect(() => {
    dispatch(markContentLoaded());
  }, [pathname, dispatch]);

  const wasOffline = useRef(false);
  useEffect(() => {
    document.body.classList.toggle('app-offline', offline);
    if (offline) {
      trackEvent('app-went-offline');
    } else if (wasOffline.current) {
      // Work queued while offline goes out now. Queued messages send from
      // their own thread (MessageThread).
      trackEvent('app-came-back-online');
      dispatch(syncQueuedMoves());
    }
    wasOffline.current = offline;
  }, [offline, dispatch]);
}

/** Android hardware back: back in the app, or exit at a tab root. */
function useBackButton() {
  const history = useHistory();
  const { pathname } = useLocation();
  const user = useSelector((state) => state.auth.user);
  // The listener is registered once; it reads the latest route from here.
  const latest = useRef();
  latest.current = { pathname, user };

  useEffect(() => trackAppHistory(history), [history]);

  useEffect(() => {
    const listener = addNativeListener('App', 'backButton', () => {
      const { pathname: path, user: currentUser } = latest.current;
      if (isTabRoot(path, currentUser) || path === '/app') {
        callNative('App', 'exitApp').catch(ignore);
        return;
      }
      goBackInApp(history, path, currentUser);
    });
    return () => {
      if (listener) listener.remove();
    };
  }, [history]);
}

/**
 * Links that would leave the app experience: Twine marketing pages (and
 * client pages, for freelancers) open in the system browser; pricing and
 * Stripe pages never open (Apple 3.1.1); production links on a staging or
 * local build stay on the build's own site.
 * Runs in the capture phase so it beats React Router's <Link> handler.
 */
function useLinkInterception() {
  const history = useHistory();
  const user = useSelector((state) => state.auth.user);
  // The listener is registered once; it reads the latest user from here.
  const latestUser = useRef();
  latestUser.current = user;

  useEffect(() => {
    const onClick = (event) => {
      if (event.defaultPrevented || event.button !== 0) return;
      const anchor = event.target.closest && event.target.closest('a[href]');
      if (!anchor) return;

      const href = anchor.getAttribute('href');
      const action = classifyAppLink(
        href,
        window.location.origin,
        latestUser.current
      );
      if (!action) return;

      event.preventDefault();
      event.stopPropagation();
      const url = new URL(href, window.location.origin);
      trackEvent('app-link-intercepted', { action, url: url.toString() });
      if (action === 'browser') {
        callNative('Browser', 'open', { url: url.toString() }).catch(() => {
          window.location.href = url.toString();
        });
      } else if (action === 'rewrite') {
        history.push(`${url.pathname}${url.search}${url.hash}`);
      }
    };
    window.addEventListener('click', onClick, true);
    return () => window.removeEventListener('click', onClick, true);
  }, [history]);
}

/** The tab bar steps aside while the keyboard is up (e.g. composers). */
function useKeyboardClass() {
  useEffect(() => {
    const listeners = [
      addNativeListener('Keyboard', 'keyboardWillShow', () =>
        document.body.classList.add('app-keyboard-open')
      ),
      addNativeListener('Keyboard', 'keyboardWillHide', () =>
        document.body.classList.remove('app-keyboard-open')
      ),
    ];
    return () => listeners.forEach((listener) => listener && listener.remove());
  }, []);
}

/**
 * body[data-app-tab] names the tab the screen belongs to, so offline CSS
 * can disable what needs a connection there (e.g. every edit on Profile).
 */
function useOwningTabAttribute() {
  const { pathname } = useLocation();
  const user = useSelector((state) => state.auth.user);
  const tab = getOwningTab(pathname, user);
  const tabId = tab ? tab.id : '';

  useEffect(() => {
    document.body.dataset.appTab = tabId;
  }, [tabId]);
}

/** App-wide behaviour for app mode. Used by NativeAppShell. */
export default function useAppBehaviour() {
  useStatusBar();
  useKeyboardClass();
  useNetworkStatus();
  useBackButton();
  useLinkInterception();
  useOwningTabAttribute();
}
